/**
 * @file Hook: useSoundEffects
 * @description Custom hook for playing sound effects
 */

import { AUDIO_ASSETS, type SfxKey } from '../constants/audioAssets';
import { useSound } from './useSound';

/**
 * Hook for playing sound effects
 * @param key The key of the sound effect from AUDIO_ASSETS.SFX
 * @returns Object with play, stop, and sound controls
 */
export function useSoundEffect(key: SfxKey) {
  const [play, { sound, stop }] = useSound(AUDIO_ASSETS.SFX[key], {
    category: 'SFX',
    interrupt: true, // Interrupt the sound if it's already playing
    loop: false, // Prevent audio from looping
  });

  return { play, stop, sound };
}

/**
 * Hook for playing a sound effect once
 * @param key The key of the sound effect from AUDIO_ASSETS.SFX
 * @param options Additional options for the sound
 * @returns Function to play the sound effect
 */
export function usePlaySoundEffect(
  key: SfxKey,
  options: { volume?: number; playbackRate?: number } = {},
) {
  const { play } = useSoundEffect(key);

  const playSound = () => {
    play(options);
  };

  return playSound;
}
